<template>
    <div class="campaign-builder-modal">
        <GoalModal
            data-qa="failed-purchase-modal-goal"
            :is-open="isOpen"
            :goal-tip-link="toolTipLink"
            :goal-tip-text="$t('funnel.edit.sideBar.goal.failedPurchase.proTip')"
            :summary-text="$t('funnel.edit.sideBar.goal.failedPurchase.summary')"
            :title="$t('funnel.edit.sideBar.goal.failedPurchase')"
            @close-modal="closeModal"
            @update-config="updateConfig"
        >
            <template #goalParameters>
                <section class="purchase-type">
                    <DsMultiselect
                        v-model="selectedFailedPurchaseType"
                        data-qa="failed-purchase-type"
                        bind-value-only
                        label-prop="label"
                        value-prop="value"
                        class="selected-purchase-type"
                        :placeholder="$t('campaign.product.purchased.purchaseTypePlaceholder')"
                        :options="availableFailedPurchaseTypes"
                        @input="resetOptions"
                    >
                        <template #option="{ option, highlight, dense, isSelected }">
                            <div
                                :data-qa="option.value"
                                class="menu-item"
                                :class="[{
                                    highlight,
                                    dense,
                                    selected: isSelected
                                }]"
                            >
                                <div>
                                    <span>{{ option.label }}</span>
                                    <br />
                                    <span v-if="option.helpText" class="option-subtext">{{ option.helpText }}</span>
                                </div>
                            </div>
                        </template>
                    </DsMultiselect>
                </section>
                <DsMultiselect
                    v-if="isOrderForm"
                    v-model="selectedOrderForm"
                    bind-value-only
                    custom-chip-label
                    searchable
                    label-prop="label"
                    value-prop="value"
                    :readonly="loading"
                    :loading="loading"
                    :force-invalid="validationErrorMessages !== null"
                    class="failed-purchase-order-form-type"
                    :placeholder="$t('funnel.edit.sideBar.goal.failedPurchase.selectOrderForm')"
                    :options="orderForms"
                    data-qa="failed-purchase-order-form"
                >
                    <template #help>
                        <span v-if="validationErrorMessages !== null">
                            {{ validationErrorMessages }}
                        </span>
                    </template>
                </DsMultiselect>
                <CustomMultiselect
                    v-if="isProduct"
                    v-model="selectedProduct"
                    bind-value-only
                    categorized
                    custom-chip-label
                    searchable
                    label-prop="chipLabel"
                    value-prop="value"
                    help-text
                    help-text-prop="subText"
                    :readonly="loading"
                    :loading="loading"
                    :force-invalid="validationErrorMessages !== null"
                    filter-categories
                    class="failed-purchase-product-type"
                    :placeholder="$t('funnel.edit.sideBar.goal.failedPurchase.selectProduct')"
                    :options="products"
                    data-qa="failed-purchase-product"
                >
                    <template #help>
                        <span v-if="validationErrorMessages !== null">
                            {{ validationErrorMessages }}
                        </span>
                    </template>
                </CustomMultiselect>
                <DsMultiselect
                    v-if="isCategory"
                    v-model="selectedCategory"
                    :force-invalid="validationErrorMessages !== null"
                    class="failed-purchase-category-type"
                    bind-value-only
                    data-qa="failed-purchase-category"
                    searchable
                    label-prop="label"
                    value-prop="value"
                    :readonly="loading"
                    :loading="loading"
                    :placeholder="$t('funnel.edit.sideBar.goal.failedPurchase.selectCategory')"
                    :options="categories"
                >
                    <template #help>
                        <span v-if="validationErrorMessages !== null">
                            {{ validationErrorMessages }}
                        </span>
                    </template>
                </DsMultiselect>
            </template>
        </GoalModal>
    </div>
</template>

<script>
import GoalModal from '../goalModal/GoalModal.vue';
import {
    loadProductList,
    loadProductCategories,
    loadOrderForms,
} from '../productPurchased/purchase.api';
import { utils } from '../../../utils/legacy-utils';
import CustomMultiselect from '@/components/shared/CustomMultiselect.vue';

export const PURCHASE_TYPE_IDS = {
    orderForm: 'orderForm',
    product: 'product',
    category: 'category',
    all: 'all',
};

export default {

    components: {
        GoalModal,
        CustomMultiselect,
    },
    PURCHASE_TYPE_IDS,

    props: {
        isOpen: {
            type: Boolean,
            required: true,
        },
        cell: {
            type: Object,
            default: () => ({
                value: {
                    failedPurchaseId: null,
                    failedPurchaseOption: null,
                    eventId: null,
                },
            }),
        },
        appInfo: {
            type: Object,
            required: true,
        },
    },

    emits: ['close-modal', 'update-cell-value'],

    data() {
        return {
            selectedFailedPurchaseType: this.cell?.value?.failedPurchaseType ?? PURCHASE_TYPE_IDS.product,
            failedPurchaseTypes: [
                {
                    value: PURCHASE_TYPE_IDS.orderForm,
                    label: this.$t('funnel.edit.sideBar.goal.failedPurchase.specificOrderForm'),
                    helpText: this.$t('funnel.edit.sideBar.goal.failedPurchase.specificOrderForm.help'),
                }, {
                    value: PURCHASE_TYPE_IDS.product,
                    label: this.$t('funnel.edit.sideBar.goal.failedPurchase.specificProduct'),
                    helpText: this.$t('funnel.edit.sideBar.goal.failedPurchase.specificProduct.help'),
                }, {
                    value: PURCHASE_TYPE_IDS.category,
                    label: this.$t('funnel.edit.sideBar.goal.failedPurchase.specificCategory'),
                    helpText: this.$t('funnel.edit.sideBar.goal.failedPurchase.specificCategory.help'),
                }, {
                    value: PURCHASE_TYPE_IDS.all,
                    label: this.$t('funnel.edit.sideBar.goal.failedPurchase.anyPurchase'),
                    helpText: this.$t('funnel.edit.sideBar.goal.failedPurchase.anyPurchase.help'),
                },
            ],
            failedPurchaseTypesProMax: [
                {
                    value: PURCHASE_TYPE_IDS.product,
                    label: this.$t('funnel.edit.sideBar.goal.failedPurchase.specificProduct'),
                    helpText: this.$t('funnel.edit.sideBar.goal.failedPurchase.specificProduct.help'),
                }, {
                    value: PURCHASE_TYPE_IDS.all,
                    label: this.$t('funnel.edit.sideBar.goal.failedPurchase.anyPurchase'),
                    helpText: this.$t('funnel.edit.sideBar.goal.failedPurchase.anyPurchase.help'),
                },
            ],
            products: [],
            categories: [],
            orderForms: [],
            selectedOrderForm: null,
            selectedProduct: null,
            selectedCategory: null,
            failedPurchaseOption: '',
            validationErrorMessages: null,
            loading: false,
        };
    },

    computed: {
        isOrderForm() {
            return this.selectedFailedPurchaseType === this.$options.PURCHASE_TYPE_IDS.orderForm;
        },
        isProduct() {
            return this.selectedFailedPurchaseType === this.$options.PURCHASE_TYPE_IDS.product;
        },
        isCategory() {
            return this.selectedFailedPurchaseType === this.$options.PURCHASE_TYPE_IDS.category;
        },
        isSpaApp() {
            return this.appInfo?.appEditionFlags?.spaApp;
        },
        toolTipLink() {
            return this.isSpaApp ? this.$t('funnel.edit.sideBar.goal.failedPurchase.keap') : this.$t('funnel.edit.sideBar.goal.failedPurchase.classic');
        },
        availableFailedPurchaseTypes() {
            return this.isSpaApp ? this.failedPurchaseTypesProMax : this.failedPurchaseTypes;
        },
    },

    async mounted() {
        await this.load();
    },

    methods: {
        async load() {
            try {
                this.loading = true;
                this.products = await loadProductList();

                if (!this.isSpaApp) {
                    this.categories = await loadProductCategories();
                    this.orderForms = await loadOrderForms();
                }

                this.loadCellValue();
            } catch (error) {
                // eslint-disable-next-line no-console
                console.error(`Could not load purchase data (products, categories, order forms): ${error}`);
            } finally {
                this.loading = false;
            }
        },

        loadCellValue() {
            this.selectedFailedPurchaseType = this.cell?.value?.failedPurchaseType ?? PURCHASE_TYPE_IDS.product;

            if ((this.selectedFailedPurchaseType === PURCHASE_TYPE_IDS.category || this.selectedFailedPurchaseType === PURCHASE_TYPE_IDS.orderForm)
                && this.isSpaApp) {
                this.selectedFailedPurchaseType = PURCHASE_TYPE_IDS.all;
                this.selectedProduct = 0;
            }

            if (this.selectedFailedPurchaseType === PURCHASE_TYPE_IDS.product) {
                const harmonizedFailedPurchaseId = String(utils.removeLongSuffix(this.cell.value?.failedPurchaseId));

                if (this.hasGroups(harmonizedFailedPurchaseId)) {
                    this.selectedProduct = `${harmonizedFailedPurchaseId}:${String(utils.removeLongSuffix(this.cell?.value?.failedPurchaseOption))}`;
                } else {
                    this.selectedProduct = harmonizedFailedPurchaseId;
                }
            } else if (this.selectedFailedPurchaseType === PURCHASE_TYPE_IDS.category) {
                this.selectedCategory = Number(utils.removeLongSuffix(this.cell.value?.failedPurchaseId));
            } else if (this.selectedFailedPurchaseType === PURCHASE_TYPE_IDS.orderForm) {
                this.selectedOrderForm = Number(utils.removeLongSuffix(this.cell.value?.failedPurchaseId));
            }
        },

        hasGroups(purchaseId) {
            const options = this.products.filter((product) => product.category.value === purchaseId && product.groups);

            return options.length > 0;
        },

        closeModal() {
            this.$emit('close-modal');
        },

        isReady() {
            const valid = (this.selectedFailedPurchaseType === PURCHASE_TYPE_IDS.all)
                || ((this.isProduct && this.selectedProduct) || (this.isCategory && this.selectedCategory)
                || (this.isOrderForm && this.selectedOrderForm));

            if (!valid) {
                this.validationErrorMessages = this.$t('funnel.edit.sideBar.goal.failedPurchase.error.productEmpty');
            }

            return valid;
        },

        updateConfig() {
            if (this.isReady()) {
                if (this.selectedFailedPurchaseType === PURCHASE_TYPE_IDS.all) {
                    this.selectedProduct = 0;
                    this.failedPurchaseOption = '0';
                } else if (this.selectedFailedPurchaseType === PURCHASE_TYPE_IDS.product) {
                    const [product, option] = this.selectedProduct.split(':');

                    if (option) {
                        this.selectedProduct = product;
                        this.failedPurchaseOption = option;
                    } else {
                        this.selectedProduct = product;
                        this.failedPurchaseOption = '0';
                    }
                } else if (this.selectedFailedPurchaseType === PURCHASE_TYPE_IDS.category) {
                    this.selectedProduct = this.selectedCategory;
                    this.failedPurchaseOption = '0';
                } else if (this.selectedFailedPurchaseType === PURCHASE_TYPE_IDS.orderForm) {
                    this.selectedProduct = this.selectedOrderForm;
                    this.failedPurchaseOption = '0';
                }

                const cell = { ...this.cell };

                cell.value = cell.value || {};

                cell.value.failedPurchaseType = this.selectedFailedPurchaseType;
                cell.value.failedPurchaseId = this.selectedProduct;
                cell.value.failedPurchaseOption = this.failedPurchaseOption;

                cell.value.eventId = this.failedPurchaseOption && this.failedPurchaseOption !== '0'
                    ? this.failedPurchaseOption
                    : this.selectedProduct;

                this.$emit('update-cell-value', { cell });
            }
        },

        resetOptions() {
            if (!this.isProduct) {
                this.selectedProduct = null;
            }

            if (!this.isCategory) {
                this.selectedCategory = null;
            }

            if (!this.isOrderForm) {
                this.selectedOrderForm = null;
            }

            // Clear validation errors when changing failed purchase type
            if (this.validationErrorMessages) {
                this.validationErrorMessages = null;
            }
        },
    },
};
</script>


<style lang="scss">
.selected-purchase-type .multiselect-single label {
    padding: 0;
}
.menu-item .option-subtext {
    font-size: $font-size-xs;
    color: $color-ink-800;
}

.menu-item.selected .option-subtext {
    color: $color-paper;
}
</style>
